import { useState } from 'react'
import { Button, Form, Select, Tag, Popconfirm, Drawer, Table, App } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { comparativeStatementsApi, rfqApi, quotationsApi } from '@/features/procurement/api'
import { plantsApi } from '@/features/masters/plants/api'
import type { ComparativeStatement } from '@/shared/types/procurement'
import DataTable from '@/shared/components/DataTable'
import FormDrawer from '@/shared/components/FormDrawer'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'

const STATUS_COLORS: Record<string, string> = { DRAFT: 'default', APPROVED: 'green' }

export default function ComparativeStatementPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const [approveForm] = Form.useForm()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [approveDrawer, setApproveDrawer] = useState(false)
  const [selectedCS, setSelectedCS] = useState<ComparativeStatement | null>(null)
  const [rfqFilter, setRfqFilter] = useState<string | undefined>()
  const [plantFilter, setPlantFilter] = useState<string | undefined>()

  const canApprove = usePermission('cs.approve')

  const { data, isLoading } = useQuery({
    queryKey: ['cs', plantFilter],
    queryFn: () => comparativeStatementsApi.list({ plant_id: plantFilter }),
  })
  const { data: plantsData } = useQuery({ queryKey: ['plants-all'], queryFn: () => plantsApi.list({ page: 1, page_size: 500 }) })
  const { data: rfqs } = useQuery({ queryKey: ['rfqs-closed'], queryFn: () => rfqApi.list({ status: 'CLOSED' }) })
  const rfqFilter2 = rfqFilter
  const { data: quotations } = useQuery({
    queryKey: ['quotations-for-cs', rfqFilter2],
    queryFn: () => quotationsApi.list({ rfq_id: rfqFilter2 }),
    enabled: !!rfqFilter2,
  })

  const plantOptions = plantsData?.items.map(p => ({ label: p.name, value: p.id })) ?? []
  const rfqOptions = rfqs?.map(r => ({ label: `${r.rfq_no} — ${r.description.slice(0, 40)}`, value: r.id })) ?? []
  const quotOptions = quotations?.map(q => ({
    label: `${q.quot_no} — ${q.supplier_name} — ₹${Number(q.total_value ?? 0).toLocaleString('en-IN')}`,
    value: q.id,
  })) ?? []

  const createMutation = useMutation({
    mutationFn: (values: Record<string, unknown>) => comparativeStatementsApi.create(values),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['cs'] }); message.success('CS created'); setDrawerOpen(false) },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Create failed'),
  })

  const approveMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      comparativeStatementsApi.approve(id, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['cs'] }); message.success('CS approved'); setApproveDrawer(false) },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Approval failed'),
  })

  const openApprove = (cs: ComparativeStatement) => { setSelectedCS(cs); setApproveDrawer(true) }

  const columns = [
    { title: 'CS No', dataIndex: 'cs_no', key: 'cs_no', width: 120 },
    { title: 'RFQ', dataIndex: 'rfq_no', key: 'rfq_no', width: 120 },
    { title: 'Plant', dataIndex: 'plant_name', key: 'plant_name' },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 100,
      render: (v: string) => <Tag color={STATUS_COLORS[v]}>{v}</Tag>,
    },
    {
      title: 'Actions', key: 'actions', width: 150,
      render: (_: unknown, r: ComparativeStatement) =>
        r.status === 'DRAFT' && canApprove
          ? <Button size="small" type="primary" onClick={() => openApprove(r)}>Select & Approve</Button>
          : r.status === 'APPROVED' ? <Tag color="green">Selected: {r.selected_quotation_id?.slice(0, 8)}</Tag> : null,
    },
  ]

  return (
    <div>
      <PageHeader
        title="Comparative Statement"
        subtitle="Compare quotations and select the best supplier"
        extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setRfqFilter(undefined); setDrawerOpen(true) }}>New CS</Button>}
      />

      <div style={{ marginBottom: 16 }}>
        <Select allowClear placeholder="Plant" options={plantOptions} onChange={v => setPlantFilter(v)} style={{ width: 180 }} />
      </div>

      <DataTable rowKey="id" columns={columns} dataSource={data} loading={isLoading} />

      <FormDrawer
        title="New Comparative Statement"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={() => form.validateFields().then(v => createMutation.mutate(v))}
        loading={createMutation.isPending}
        form={form}
      >
        <Form.Item name="plant_id" label="Plant" rules={[{ required: true }]}>
          <Select options={plantOptions} />
        </Form.Item>
        <Form.Item name="rfq_id" label="RFQ" rules={[{ required: true }]}>
          <Select options={rfqOptions} showSearch optionFilterProp="label" onChange={v => setRfqFilter(v)} />
        </Form.Item>
        <Form.Item name="remarks" label="Remarks">
          <Select.Option value="">—</Select.Option>
        </Form.Item>
      </FormDrawer>

      <Drawer
        title="Select Winning Quotation"
        open={approveDrawer}
        onClose={() => setApproveDrawer(false)}
        width={480}
        footer={
          <Button type="primary" onClick={() => approveForm.validateFields().then(v =>
            approveMutation.mutate({ id: selectedCS!.id, data: v })
          )}>Approve & Select</Button>
        }
      >
        {selectedCS && (
          <Form form={approveForm} layout="vertical">
            <Form.Item name="selected_quotation_id" label="Selected Quotation" rules={[{ required: true }]}>
              <Select options={quotOptions} showSearch optionFilterProp="label" />
            </Form.Item>
            <Form.Item name="remarks" label="Remarks">
              <Select.Option value="">—</Select.Option>
            </Form.Item>
          </Form>
        )}
      </Drawer>
    </div>
  )
}
